/**
 * Centralized error handling utility
 * Manages network errors, API errors, and unexpected JavaScript exceptions
 */

import { AxiosError } from 'axios';

const isDevelopment = import.meta.env.VITE_APP_ENV === 'development';

/**
 * Error types enumeration
 */
export const ErrorTypes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  API_ERROR: 'API_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorType = typeof ErrorTypes[keyof typeof ErrorTypes];

/**
 * Default error messages for user-friendly display
 */
const DEFAULT_ERROR_MESSAGES: Record<ErrorType, string> = {
  [ErrorTypes.NETWORK_ERROR]: 'Network connection failed. Please check your internet connection and try again.',
  [ErrorTypes.API_ERROR]: 'Something went wrong on our end. Please try again later.',
  [ErrorTypes.AUTHENTICATION_ERROR]: 'Authentication failed. Please log in again.',
  [ErrorTypes.VALIDATION_ERROR]: 'Please check your input and try again.',
  [ErrorTypes.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.',
};

/**
 * Enhanced error class with additional context
 */
export class AppError extends Error {
  type: ErrorType;
  details: any;
  timestamp: string;

  constructor(message: string, type: ErrorType = ErrorTypes.UNKNOWN_ERROR, details: any = null) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Handle API errors from axios responses
 */
export const handleApiError = (error: AxiosError | Error): AppError => {
  let errorType: ErrorType = ErrorTypes.API_ERROR;
  let userMessage = DEFAULT_ERROR_MESSAGES[ErrorTypes.API_ERROR];
  let details: any = null;

  if ('response' in error && error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    details = { status, data };

    switch (status) {
      case 401:
      case 403:
        errorType = ErrorTypes.AUTHENTICATION_ERROR;
        userMessage = DEFAULT_ERROR_MESSAGES[ErrorTypes.AUTHENTICATION_ERROR];
        break;
      case 400:
        errorType = ErrorTypes.VALIDATION_ERROR;
        userMessage = (data as any)?.message || DEFAULT_ERROR_MESSAGES[ErrorTypes.VALIDATION_ERROR];
        break;
      case 404:
        userMessage = 'The requested resource was not found.';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        userMessage = 'Server error. Please try again later.';
        break;
      default:
        userMessage = (data as any)?.message || DEFAULT_ERROR_MESSAGES[ErrorTypes.API_ERROR];
    }
  } else if ('request' in error && error.request) {
    // Network error
    errorType = ErrorTypes.NETWORK_ERROR;
    userMessage = DEFAULT_ERROR_MESSAGES[ErrorTypes.NETWORK_ERROR];
    details = { request: error.request };
  } else {
    // Other error
    details = { message: error.message };
  }

  const appError = new AppError(userMessage, errorType, details);
  
  // Log detailed error only in development
  if (isDevelopment) {
    console.error('API Error Details:', {
      original: error,
      processed: appError,
    });
  }

  return appError;
};

/**
 * Handle general JavaScript errors
 */
export const handleGenericError = (error: Error, context: any = null): AppError => {
  const appError = new AppError(
    DEFAULT_ERROR_MESSAGES[ErrorTypes.UNKNOWN_ERROR],
    ErrorTypes.UNKNOWN_ERROR,
    { originalError: error.message, context }
  );

  // Log detailed error only in development
  if (isDevelopment) {
    console.error('Generic Error Details:', {
      original: error,
      context,
      processed: appError,
    });
  }

  return appError;
};

/**
 * Log error for monitoring (placeholder for future integration)
 */
export const logError = (error: AppError | Error, context: Record<string, any> | null = null): void => {
  if (isDevelopment) {
    console.error('Error logged:', { error, context });
  }
  
  // TODO: Integrate with error monitoring service (e.g., Sentry, LogRocket)
  // errorMonitoringService.captureException(error, { context });
};

/**
 * Utility to check if error is of specific type
 */
export const isErrorOfType = (error: unknown, type: ErrorType): boolean => {
  return error instanceof AppError && error.type === type;
};

/**
 * Get user-friendly error message
 */
export const getUserErrorMessage = (error: unknown): string => {
  if (error instanceof AppError) {
    return error.message;
  }
  return DEFAULT_ERROR_MESSAGES[ErrorTypes.UNKNOWN_ERROR];
};

export default {
  ErrorTypes,
  AppError,
  handleApiError,
  handleGenericError,
  logError,
  isErrorOfType,
  getUserErrorMessage,
};